import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useT } from '../i18n';

interface Connection {
  id: string;
  name: string;
  instanceName: string;
  provider: string;
  status: string;
  phone: string | null;
  profileName: string | null;
  inboxEnabled: boolean;
  company: string | null;
  companyActive: boolean | null;
  sentToday: number;
  lastSentAt: string | null;
  updatedAt: string;
}

interface Payload {
  total: number;
  connected: number;
  waitingQr: number;
  offline: number;
  items: Connection[];
}

type Filter = 'all' | 'connected' | 'waitingQr' | 'offline';

const STATUS_STYLE: Record<string, { key: string; className: string }> = {
  WORKING: { key: 'pc.connected', className: 'bg-emerald-100 text-emerald-800' },
  SCAN_QR_CODE: { key: 'pc.waitingQr', className: 'bg-amber-100 text-amber-800' },
};

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const formatNumber = (value: number) => value.toLocaleString('en-US');

/** Todos os números da plataforma, de todas as empresas, em uma tabela só */
export function PlatformConnections() {
  const t = useT();
  const [data, setData] = useState<Payload | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/system/connections', { headers: authHeaders() });
      if (!response.ok) throw new Error(String(response.status));
      setData(await response.json());
    } catch {
      toast.error(t('pc.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    // Um número pode cair a qualquer momento: a tela se atualiza sozinha
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  if (!data) {
    return loading ? <p className="text-center text-gray-400 py-8">{t('common.loading')}</p> : null;
  }

  const rows = data.items.filter(item => {
    if (filter === 'connected') return item.status === 'WORKING';
    if (filter === 'waitingQr') return item.status === 'SCAN_QR_CODE';
    if (filter === 'offline') return item.status !== 'WORKING' && item.status !== 'SCAN_QR_CODE';
    return true;
  });

  const counts: Record<Filter, number> = {
    all: data.total, connected: data.connected, waitingQr: data.waitingQr, offline: data.offline,
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b">
        <div>
          <h3 className="font-semibold text-gray-900">{t('pc.title')}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{t('pc.subtitle')}</p>
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {(['all', 'connected', 'waitingQr', 'offline'] as const).map(option => (
            <button
              key={option}
              onClick={() => setFilter(option)}
              className={`px-3 py-1 text-xs rounded-md whitespace-nowrap ${filter === option ? 'bg-white shadow-sm font-semibold' : 'text-gray-600'}`}
            >
              {option === 'all' ? t('pc.filterAll') : t(`pc.${option}`)} ({formatNumber(counts[option])})
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="p-10 text-center text-sm text-gray-400">{t('pc.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-start px-4 py-2">{t('pc.colNumber')}</th>
                <th className="text-start px-4 py-2">{t('pc.colCompany')}</th>
                <th className="text-start px-4 py-2">{t('pc.colStatus')}</th>
                <th className="text-start px-4 py-2">{t('pc.colInbox')}</th>
                <th className="text-start px-4 py-2">{t('pc.colToday')}</th>
                <th className="text-start px-4 py-2">{t('pc.colLastSend')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const status = STATUS_STYLE[row.status];
                return (
                  <tr key={row.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {row.phone ? (
                        <>
                          <div className="font-medium text-gray-900" dir="ltr">+{row.phone}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[220px]" dir="auto">
                            {row.profileName || row.name}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-gray-500" dir="auto">{row.name}</div>
                          <div className="text-xs text-gray-400">{t('pc.noPhone')}</div>
                        </>
                      )}
                    </td>

                    <td className="px-4 py-2">
                      <span className="text-gray-700" dir="auto">{row.company || '—'}</span>
                      {row.companyActive === false && (
                        <span className="block text-[11px] text-red-600">{t('pc.companySuspended')}</span>
                      )}
                    </td>

                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status?.className || 'bg-gray-100 text-gray-700'}`}>
                        {status ? t(status.key) : t('pc.offline')}
                      </span>
                    </td>

                    <td className="px-4 py-2">
                      {row.inboxEnabled
                        ? <span className="text-emerald-700">✓</span>
                        : <span className="text-gray-300">—</span>}
                    </td>

                    <td className="px-4 py-2 text-gray-700">{formatNumber(row.sentToday)}</td>

                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                      {row.lastSentAt
                        ? <bdi dir="ltr">{new Date(row.lastSentAt).toLocaleDateString()}</bdi>
                        : t('pc.never')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
